/**
 * Per-account client state, and wiping it when the account changes.
 *
 * Two people can use the same browser tab one after the other (a new
 * business signs up, logs out, an existing one logs in). Anything the portal
 * kept for the first account must not reach the second: list filters and
 * scroll positions that carry branch ids, the "where you were going" redirect,
 * any old onboarding flag.
 *
 * What is NOT here, on purpose:
 * - the session itself (tokens, cached profile, push token) — api.js
 *   `clearTokens()` and push.js `resetPush()` already drop those;
 * - browser-wide preferences — UI language (`businessLang`), theme
 *   (`puralance.business.theme`), "already asked for notification permission";
 * - state keyed by account already — report column choices
 *   (`pl.reports.columns.{userId}.*`) and the onboarding dismiss
 *   (`pl.onboarding.dismissed.{businessId}.{userId}`), which belong to their
 *   own account and come back when that account signs in again.
 *
 * Everything in memory (contexts, page state) lives under MainLayout, which
 * unmounts on logout; there is no query cache.
 */

/** sessionStorage keys that hold one account's view of the portal. */
const SESSION_PREFIXES = [
  "list-url:", // listMemory: a list page's last query string (filters may name branches)
  "scroll:", // useScrollRestoration: scroll offset per history entry
];

/** Flags older builds kept globally; removed wherever they are found. */
const LEGACY_KEYS = ["businessWelcome"];

/** Who was signed in before the login screen, to tell a return from a switch. */
const LAST_ACCOUNT_KEY = "pl.lastAccount";

/** `businessId:userId` for a profile, or "" without one. */
export const accountTag = (user) => (user?.id ? `${user.business?.id ?? ""}:${user.id}` : "");

const removeMatching = (storage, test) => {
  try {
    const doomed = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && test(key)) doomed.push(key);
    }
    doomed.forEach((key) => storage.removeItem(key));
  } catch {
    // Storage unavailable: there is nothing kept to leak either.
  }
};

/**
 * Drop every per-account key. Called on logout, when a session ends on its
 * own, and again on login (a session may have ended in another tab without
 * this one noticing).
 */
export const forgetAccountState = () => {
  removeMatching(window.sessionStorage, (key) => SESSION_PREFIXES.some((p) => key.startsWith(p)) || LEGACY_KEYS.includes(key));
  removeMatching(window.localStorage, (key) => LEGACY_KEYS.includes(key));
};

/** Before the session goes: note whose it was (see takeLastAccount). */
export const rememberLeavingAccount = (user) => {
  const tag = accountTag(user);
  try {
    if (tag) sessionStorage.setItem(LAST_ACCOUNT_KEY, tag);
  } catch {
    // Without it, a post-login redirect is simply not trusted (see App).
  }
};

/** Who left this tab last ("" if nobody did), read once and forgotten. */
export const takeLastAccount = () => {
  try {
    const tag = sessionStorage.getItem(LAST_ACCOUNT_KEY) ?? "";
    sessionStorage.removeItem(LAST_ACCOUNT_KEY);
    return tag;
  } catch {
    return "";
  }
};

/** Where the onboarding banner's dismiss is kept: per business and per user. */
export const onboardingDismissKey = (user) =>
  user?.id && user?.business?.id ? `pl.onboarding.dismissed.${user.business.id}.${user.id}` : null;
